/**
 * Copy deck for the "route to a human" affordance (help catch-up Tier 1 #14).
 *
 * Authored as a KEY DECK from the first line, not as JSX literals — DEC-053
 * schedules a droom i18n runtime and every screen written as prose in a
 * component is a screen that has to be rewritten before it can be translated.
 * Same rules as `documentSteps.ts`:
 *
 *   • FULL-SENTENCE keys, at most one interpolation slot, never fragment joins
 *     (Hindi/Arabic word order differs; a joined fragment can never translate).
 *   • Customer tone: plain, warm, second person, no jargon, no blame.
 *   • No claim the product cannot keep. We do NOT promise a response time, and
 *     we never imply support can approve or reject a verification.
 */

export const SUPPORT_COPY = {
  // ── the affordance itself ──────────────────────────────────────────────────
  'droom.support.trigger': 'Having trouble?',
  'droom.support.title': 'Get help with this step',
  'droom.support.close': 'Close',

  // ── the honest lines about WHO can help, and with what ─────────────────────
  'droom.support.intro.named': 'If something here is not working, {tenantName} can help you.',
  'droom.support.intro.generic': 'If something here is not working, help is available.',
  /**
   * The honest fallback when a tenant has configured no support channel. It says
   * what IS true (the invite came from somewhere; the verifier is a person)
   * rather than inventing a contact — a dead phone number is worse than none.
   */
  'droom.support.none':
    'This organisation has not listed a support contact here. Reply to the message that sent you this link, and a person will help you.',
  'droom.support.boundary':
    'Support can help you use this page. Only your verifier decides the outcome of your verification, on the call.',

  // ── channels ───────────────────────────────────────────────────────────────
  'droom.support.phone.label': 'Call support',
  'droom.support.email.label': 'Email support',
  'droom.support.web.label': 'Open the help page',
  'droom.support.hours': 'Available {hours}',

  // ── during the call, the route to a human IS the human ─────────────────────
  'droom.support.oncall':
    'You are on a call with your verifier now — ask them directly and they will help you.',

  // ── per-step hints: what to try on THIS screen, before contacting anyone ───
  'droom.support.hint.welcome': 'Check you opened the most recent link we sent you.',
  'droom.support.hint.schedule': 'Pick any slot that suits you — you can reschedule later.',
  'droom.support.hint.consent':
    'You need to agree before we can continue. Read it fully — nothing is shared until you agree.',
  'droom.support.hint.confirm':
    'Check each detail carefully. If something is wrong, tell your verification officer on the call.',
  'droom.support.hint.pan': 'Enter your PAN exactly as printed, with no spaces.',
  'droom.support.hint.aadhaar': 'Use the OTP sent to the mobile number linked with your Aadhaar.',
  'droom.support.hint.capture':
    'Find even light, hold your phone steady, and keep the whole document inside the frame.',
  'droom.support.hint.selfie': 'Face the camera in even light and keep your whole face in the oval.',
  'droom.support.hint.waiting':
    'Keep this page open — your verifier joins here. Leaving may end your session.',
  'droom.support.hint.complete': 'Nothing more to do here. You can close this page.',
  'droom.support.hint.generic': 'Try again on a stronger connection, or use a different network.',
} as const;

export type SupportCopyKey = keyof typeof SUPPORT_COPY;

/** Look up a deck string. Missing keys surface as the key — never a blank. */
export function supportCopy(key: SupportCopyKey): string {
  return SUPPORT_COPY[key] ?? key;
}

/**
 * The per-step hint key for a flow step. Falls back to the generic line rather
 * than rendering nothing, because "Having trouble?" that opens to an empty panel
 * is worse than no affordance.
 */
export function hintKeyForStep(step: string): SupportCopyKey {
  const direct = `droom.support.hint.${step}` as SupportCopyKey;
  if (direct in SUPPORT_COPY) return direct;
  // Plan-derived check steps arrive as `check:<CHECK_KEY>`; map the common ones.
  if (step.startsWith('check:')) {
    const key = step.slice('check:'.length).toLowerCase();
    if (key.includes('selfie')) return 'droom.support.hint.selfie';
    if (key.includes('pan')) return 'droom.support.hint.pan';
    if (key.includes('aadhaar')) return 'droom.support.hint.aadhaar';
    if (key.includes('doc')) return 'droom.support.hint.capture';
  }
  return 'droom.support.hint.generic';
}
